import { useState, useEffect, useRef } from 'react';
import BusinessRegistrationForm from './BusinessRegistrationForm';
import PhotoGalleryUpload from './PhotoGalleryUpload';
import HoursOfOperationForm from './HoursOfOperationForm';

/**
 * AdminBusinessRegistration
 * Right-side drawer wrapper for business registration form
 * Combines all form components (basic info, photos, hours)
 * 
 * Props:
 * - isOpen: boolean - Whether drawer is visible
 * - onClose: () => void - Called when drawer should close
 * - onSubmit: (data) => Promise - Called when user submits form
 * - initialData?: Object - Pre-filled data for edit mode
 * - isLoading?: boolean - Show loading state
 * - categories: [{id, name}, ...] - Category dropdown options
 * - title?: string - Drawer title
 */
const AdminBusinessRegistration = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  isLoading = false,
  categories = [],
  title = initialData ? 'Edit Business' : 'Register Business',
}) => {
  const formRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [photos, setPhotos] = useState(initialData?.photos || []);
  const [hours, setHours] = useState(initialData?.opening_hours || null);
  const [toast, setToast] = useState(null);
  const [isStep1Valid, setIsStep1Valid] = useState(!!initialData);

  // Reset state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setPhotos(initialData?.photos || []);
      setHours(initialData?.opening_hours || null);
      setToast(null);
      formRef.current?.reset(initialData);
      // Check initial validity
      setTimeout(() => {
        if (formRef.current?.isValid) {
          setIsStep1Valid(formRef.current.isValid());
        }
      }, 0);
    }
  }, [isOpen]);

  /**
   * Show toast notification
   */
  const flash = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  /**
   * Handle form submission
   */
  const handleFormSubmit = async (basicFormData) => {
    // Build complete data; omit fields the backend schema doesn't accept:
    // - photos: stored separately in Supabase, not part of the business record payload
    // - opening_hours: omit entirely when null (Joi.object() rejects null)
    const { photos: _photos, ...restFormData } = basicFormData;
    const completeData = {
      ...restFormData,
      ...(hours && typeof hours === 'object' ? { opening_hours: hours } : {}),
    };

    try {
      await onSubmit(completeData);
      flash('success', initialData ? 'Business updated successfully.' : 'Business created successfully.');
      onClose();
    } catch (error) {
      const errorMessage = error.message || 'Failed to save business';
      flash('error', errorMessage);
    }
  };

  // Header configs
  const STEPS = [
    { id: 1, label: 'Basic Info', icon: '🏢' },
    { id: 2, label: 'Photos', icon: '📸' },
    { id: 3, label: 'Hours', icon: '🕐' },
  ];

  return (
    <>
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-5 right-5 z-[70] flex items-center gap-2.5 rounded-xl px-4 py-3 text-[13px] font-medium border shadow-lg shadow-black/10 transition-all
          ${toast.type === 'success' ? 'bg-white border-emerald-200 text-emerald-700' : 'bg-white border-red-200 text-red-600'}`}>
          {toast.type === 'success'
            ? <svg className="shrink-0 text-emerald-500" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            : <svg className="shrink-0 text-red-500" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          }
          {toast.msg}
        </div>
      )}

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 bottom-0 w-full max-w-[500px] bg-white shadow-xl z-40 transform transition-all duration-300 flex flex-col
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* ── Header ── */}
        <div className="flex-shrink-0 border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[15px] font-bold text-slate-900">{title}</h2>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex gap-2">
            {STEPS.map((step) => {
              const isLocked = step.id > 1 && !isStep1Valid;
              return (
                <button
                  key={step.id}
                  onClick={() => {
                    if (isLocked) {
                      flash('error', 'Please fill in all required fields before proceeding.');
                      return;
                    }
                    setCurrentStep(step.id);
                  }}
                  type="button"
                  disabled={isLoading}
                  title={isLocked ? 'Complete Basic Info first' : step.label}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all disabled:cursor-not-allowed
                    ${currentStep === step.id
                      ? 'bg-blue-500 text-white shadow-sm'
                      : isLocked
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    } ${isLoading ? 'opacity-60' : ''}`}
                >
                  {step.icon} {step.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Content (Scrollable) ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Always mounted so state survives step navigation; hidden with CSS when not active */}
          <div className={currentStep !== 1 ? 'hidden' : ''}>
            <h3 className="text-[13px] font-semibold text-slate-800 mb-4">
              Basic Information
            </h3>
            <BusinessRegistrationForm
              ref={formRef}
              initialData={initialData}
              categories={categories}
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
              onCancel={onClose}
              onFormChange={() => {
                // Check validity whenever form changes
                if (formRef.current?.isValid) {
                  setIsStep1Valid(formRef.current.isValid());
                }
              }}
            />
          </div>

          {currentStep === 2 && (
            <div>
              <h3 className="text-[13px] font-semibold text-slate-800 mb-4">
                Business Photos
              </h3>
              <PhotoGalleryUpload
                photos={photos}
                onPhotosChange={setPhotos}
                maxPhotos={10}
              />
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h3 className="text-[13px] font-semibold text-slate-800 mb-4">
                Operating Hours
              </h3>
              <HoursOfOperationForm
                hours={hours}
                onHoursChange={setHours}
              />
            </div>
          )}
        </div>

        {/* ── Footer (Sticky) ── */}
        <div className="flex-shrink-0 border-t border-slate-200 px-6 py-4 bg-slate-50 flex gap-3 items-center justify-between">
          <div className="flex gap-2 flex-1">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep - 1)}
                disabled={isLoading}
                className="flex-1 px-4 py-2 text-[12px] font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Back
              </button>
            )}

            {currentStep < STEPS.length && (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={isLoading || (currentStep === 1 && !isStep1Valid)}
                className="flex-1 px-4 py-2 text-[12px] font-medium text-white bg-slate-600 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            )}
          </div>

          {currentStep === STEPS.length && (
            <div className="flex gap-2 flex-1">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-2 text-[12px] font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => formRef.current?.submitForm()}
                disabled={isLoading}
                className="flex-1 px-4 py-2 text-[12px] font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Register Business'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminBusinessRegistration;
